"use client"

import { Button } from "@/components/ui/button"
import { LeoIcon } from "@/components/ui/leo-icon"
import { STATUS_BADGE_TONE_CLASS } from "@/lib/list-status-badges"
import {
  authoringConfidenceScoreLineItem,
  qualitySuggestionConfidenceScore,
  confidenceDelta,
  isPerformanceCheckId,
  isVariationCheckId,
  qualitySuggestionLines,
  questionQualityFieldSiblings,
  selectHomeRecommendations,
  LEO_DISMISS_LABEL,
  leoDismissFindingAria,
  leoModifyFindingAria,
  SELECTION_ENHANCE_CHECK_ID,
  type QualitySuggestionLine,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
  type QuestionQualitySnapshot,
} from "@/lib/question-quality"
import { cn } from "@/lib/utils"
import { FindingCopyEmphasis, FindingTitleEmphasis } from "@/components/question-quality/finding-copy-emphasis"
import { QuestionQualitySuggestionChips } from "@/components/question-quality/question-quality-suggestion-chip"
import { useLeoApplyMotion } from "@/components/question-quality/leo-apply-motion"
import { useLeoThreadCatalogState } from "@/components/question-quality/leo-thread-catalog-context"
import type { QuestionQualityHistory } from "@/lib/question-quality/types"

export const REVIEW_WASH =
  "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_8%,transparent),color-mix(in_oklch,var(--exxat-color-danger,var(--destructive))_3%,transparent))]"
/** @deprecated Severity is internal; canvas and popover shells use {@link LEO_SPOTTED_SURFACE_WASH}. */
export const CAUTION_WASH =
  "bg-[linear-gradient(180deg,color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_10%,transparent),color-mix(in_oklch,var(--exxat-color-warning,var(--chart-4))_3%,transparent))]"
/** One outer-shell wash for every Leo spotted mark (caution and review). */
export const LEO_SPOTTED_SURFACE_WASH = REVIEW_WASH

export function ConfidenceMeter({
  value,
  compareTo,
  now,
  suggested,
  lineItem,
}: {
  value: number
  compareTo?: number
  now?: boolean
  /** Applyable Leo suggestion: full confidence in this fix. */
  suggested?: boolean
  /** Now meter on one field home, not the whole item. */
  lineItem?: boolean
}) {
  const safe = Math.max(0, Math.min(100, value))
  const delta = compareTo == null ? "same" : confidenceDelta(safe, compareTo)
  const nowLabel = lineItem
    ? `Authoring confidence for this field now ${safe}%`
    : `Authoring confidence now ${safe}%`
  const label = suggested
    ? compareTo == null
      ? `Leo confidence in this suggestion ${safe}%`
      : delta === "up"
        ? `Leo confidence in this suggestion ${safe}%, up from ${compareTo}% on this field`
        : delta === "down"
          ? `Leo confidence in this suggestion ${safe}%, down from ${compareTo}% on this field`
          : `Leo confidence in this suggestion ${safe}%`
    : now
      ? nowLabel
      : delta === "up"
        ? `Authoring confidence ${safe}% after this, up from ${compareTo}%`
        : delta === "down"
          ? `Authoring confidence ${safe}% after this, down from ${compareTo}%`
          : `Authoring confidence ${safe}% after this`
  const valueClass =
    suggested && delta === "up"
      ? STATUS_BADGE_TONE_CLASS.success
      : now || delta === "same"
        ? "border border-border bg-surface-2 text-foreground"
        : delta === "up"
          ? STATUS_BADGE_TONE_CLASS.success
          : STATUS_BADGE_TONE_CLASS.danger
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        valueClass,
      )}
      aria-label={label}
    >
      <i className="fa-light fa-gauge text-[0.7rem]" aria-hidden="true" />
      {safe}%
      {delta === "up" ? (
        <i className="fa-light fa-arrow-up text-[0.65rem]" aria-hidden="true" />
      ) : null}
      {delta === "down" ? (
        <i className="fa-light fa-arrow-down text-[0.65rem]" aria-hidden="true" />
      ) : null}
    </span>
  )
}

function optionMark(id: string | undefined): string | null {
  if (!id) return null
  const letter = id.trim()
  if (letter.length === 1 && /[a-z]/i.test(letter)) return letter.toUpperCase()
  return null
}

export const LEO_OPTION_SET_CARD_CLASS =
  "min-w-0 rounded-2 border border-border bg-surface-2 p-3"

export function QualitySuggestionPreview({
  lines,
  appliedSet = false,
}: {
  lines: QualitySuggestionLine[]
  /** Post-apply: full final set; changed rows read louder than kept options. */
  appliedSet?: boolean
}) {
  return (
    <ul className="m-0 min-w-0 flex-1 list-none space-y-2 p-0">
      {lines.map((line) => {
        const mark = optionMark(line.optionId)
        const changed = Boolean(line.replaces || line.isNew)
        const note = line.replaces
          ? `Replaces ${line.replaces}`
          : line.isNew
            ? "New option"
            : line.isKept
              ? "Correct option"
              : undefined
        const label = note ? `${line.text}. ${note}` : line.text
        return (
          <li
            key={`${line.optionId ?? "new"}:${line.text}`}
            className="list-none"
          >
            <p
              className="flex items-start gap-2 text-sm leading-snug"
              aria-label={label}
            >
              {mark ? (
                <span
                  className={cn(
                    "mt-0.5 w-4 shrink-0 text-center text-[0.7rem] font-medium",
                    appliedSet && changed
                      ? "text-brand"
                      : "text-muted-foreground",
                  )}
                  aria-hidden="true"
                >
                  {mark}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block text-foreground",
                    appliedSet && changed
                      ? "font-semibold"
                      : "font-medium",
                  )}
                >
                  {line.text}
                </span>
                {note ? (
                  <span
                    className={cn(
                      "mt-0.5 block text-[0.7rem] font-normal",
                      appliedSet && changed
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {line.replaces ? (
                      <>
                        Replaces{" "}
                        <FindingCopyEmphasis
                          text={line.replaces}
                          phrases={[line.replaces]}
                        />
                      </>
                    ) : (
                      note
                    )}
                  </span>
                ) : null}
              </span>
            </p>
          </li>
        )
      })}
    </ul>
  )
}

export function QuestionQualityFinding({
  check,
  siblingChecks,
  surface = "hover",
  bleedTop = true,
  quiet = false,
}: {
  check: QuestionQualityCheck
  siblingChecks?: QuestionQualityCheck[]
  surface?: "hover" | "panel"
  bleedTop?: boolean
  quiet?: boolean
}) {
  const review = check.severity === "review"
  const nowScore = authoringConfidenceScoreLineItem(check)

  if (quiet) {
    return (
      <section aria-label={check.title} className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-normal leading-snug text-foreground">
            <FindingTitleEmphasis check={check} />
          </p>
          {surface === "panel" ? (
            <ConfidenceMeter value={nowScore} now lineItem />
          ) : null}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {check.mechanism}
        </p>
      </section>
    )
  }

  return (
    <section
      aria-label={check.title}
      className={cn(
        "space-y-2",
        surface === "hover" && bleedTop
          ? "-mx-4 -mt-4 px-4 pb-3 pt-4"
          : "rounded-2 px-3 py-3",
        LEO_SPOTTED_SURFACE_WASH,
      )}
    >
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-base font-normal leading-snug">
            <FindingTitleEmphasis check={check} />
          </p>
          <ConfidenceMeter value={nowScore} now lineItem />
        </div>
        <p className="text-[0.7rem] font-normal leading-snug text-muted-foreground">
          {review ? "Blocks Ready" : "Does not block Ready"}
        </p>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {check.mechanism}
      </p>
    </section>
  )
}

function canModifyAlternate(
  check: QuestionQualityCheck,
  alternate: QuestionQualityAlternate,
): boolean {
  if (
    alternate.actionKind === "open-leo" ||
    alternate.actionKind === "none" ||
    !alternate.patch
  ) {
    return false
  }
  return isVariationCheckId(check.id) || check.id === SELECTION_ENHANCE_CHECK_ID
}

function isActionOnlyRecommendation(
  check: QuestionQualityCheck,
  recs: QuestionQualityAlternate[],
): boolean {
  return recs.every(
    (alternate) => qualitySuggestionLines(check, alternate).length === 0,
  )
}

export function QuestionQualitySuggestedEnhancements({
  check,
  siblingChecks,
  snapshot,
  history = null,
  onUse,
  onModify,
  onDismissCard,
  onUndoApply,
  disabled,
  recs,
  resolveActionLabel,
  heroStack = false,
  outlineApplyCta = false,
  compact = false,
}: {
  check: QuestionQualityCheck
  siblingChecks?: QuestionQualityCheck[]
  snapshot?: QuestionQualitySnapshot
  history?: QuestionQualityHistory | null
  onUse: (secondary?: boolean, alternate?: QuestionQualityAlternate) => void
  onModify?: (alternate: QuestionQualityAlternate) => void
  /** Canvas hover and Ask Leo pager: Dismiss beside the apply CTA. */
  onDismissCard?: () => void
  /** Thread variation: restore canvas + reopen the frozen card set. */
  onUndoApply?: (alternate: QuestionQualityAlternate) => void
  disabled?: boolean
  recs?: QuestionQualityAlternate[]
  resolveActionLabel?: (alternate: QuestionQualityAlternate) => string
  /** Ask Leo hero: one full-width row per recommendation, no inner pager. */
  heroStack?: boolean
  /** Ask Leo hero: outline apply buttons until selected or applying. */
  outlineApplyCta?: boolean
  /** Inspector setting popovers: single-column, tighter cards. */
  compact?: boolean
}) {
  const openChecks =
    siblingChecks && siblingChecks.length > 0 ? siblingChecks : [check]
  const textRecs =
    recs ??
    selectHomeRecommendations(
      questionQualityFieldSiblings(openChecks, check),
      openChecks,
    )
  const { frozen: catalogFrozen, appliedAlternateId } =
    useLeoThreadCatalogState()
  const nowScore = authoringConfidenceScoreLineItem(check)
  const showConfidence = !isPerformanceCheckId(check.id)
  const afterById = new Map(
    textRecs.map((alternate) => [
      alternate.id,
      qualitySuggestionConfidenceScore(openChecks, alternate),
    ]),
  )
  const singleApply = textRecs.length === 1

  if (textRecs.length === 0) return null

  if (isActionOnlyRecommendation(check, textRecs)) {
    return (
      <QuestionQualitySuggestionChips
        check={check}
        history={history}
        recs={textRecs}
        disabled={disabled}
        onUse={(alternate) =>
          onUse(
            alternate.actionKind === check.secondaryActionKind,
            alternate,
          )
        }
      />
    )
  }

  return (
    <section className="space-y-2" aria-label="Suggested options">
      <div
        className={cn(
          "grid items-start gap-2",
          heroStack
            ? "grid-cols-1"
            : textRecs.length > 1 && "@min-[32rem]/qq-hover:grid-cols-2",
        )}
      >
        {textRecs.map((alternate) => {
          const after = afterById.get(alternate.id) ?? nowScore
          const optionLines = qualitySuggestionLines(check, alternate, snapshot)
          const selected =
            catalogFrozen && appliedAlternateId === alternate.id
          const ctaDisabled = disabled || catalogFrozen
          const showApplyCta = !catalogFrozen || selected
          return (
            <div
              key={alternate.id}
              className={cn(
                compact ? "space-y-2.5" : "space-y-2",
                !compact && LEO_OPTION_SET_CARD_CLASS,
              )}
            >
              <div className="flex items-start justify-between gap-3">
                {optionLines.length > 0 ? (
                  <QualitySuggestionPreview
                    lines={optionLines}
                    appliedSet={selected}
                  />
                ) : (
                  <p className="min-w-0 flex-1 text-sm font-medium leading-snug">
                    {alternate.text}
                  </p>
                )}
                {showConfidence ? (
                  <ConfidenceMeter
                    value={after}
                    compareTo={nowScore}
                    suggested
                  />
                ) : null}
              </div>
              {showApplyCta ? (
                <div className="flex flex-wrap items-center gap-2">
                  <LeoApplyCta
                    label={
                      resolveActionLabel?.(alternate) ?? alternate.actionLabel
                    }
                    applyKey={alternate.id}
                    variant={
                      selected
                        ? "default"
                        : outlineApplyCta
                          ? "outline"
                          : singleApply
                            ? "default"
                            : "outline"
                    }
                    disabled={ctaDisabled || selected}
                    threadHandoff={outlineApplyCta}
                    ariaCurrent={selected ? "true" : undefined}
                    onUse={() =>
                      onUse(
                        alternate.actionKind === check.secondaryActionKind,
                        alternate,
                      )
                    }
                  />
                  {selected && onUndoApply ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => onUndoApply(alternate)}
                    >
                      Undo
                    </Button>
                  ) : onDismissCard ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={ctaDisabled}
                      aria-label={leoDismissFindingAria(check.title)}
                      onClick={onDismissCard}
                    >
                      {LEO_DISMISS_LABEL}
                    </Button>
                  ) : onModify && canModifyAlternate(check, alternate) ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={ctaDisabled}
                      aria-label={leoModifyFindingAria(
                        alternate.actionLabel ?? alternate.text,
                      )}
                      onClick={() => onModify(alternate)}
                    >
                      Modify with Leo
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function LeoApplyCta({
  label,
  applyKey,
  variant,
  disabled,
  threadHandoff = false,
  ariaCurrent,
  onUse,
}: {
  label: string
  applyKey: string
  variant: "default" | "outline"
  disabled?: boolean
  /** Ask Leo hero: thinking lives in the thread, not on this chip. */
  threadHandoff?: boolean
  ariaCurrent?: "true"
  onUse: () => void
}) {
  const { captureSource, applyingKey, applyingMotion } = useLeoApplyMotion()
  const applying = !threadHandoff && applyingKey === applyKey
  const busy = applying && applyingMotion != null
  const answered = applying && applyingMotion === "answered"
  return (
    <Button
      type="button"
      size="sm"
      variant={answered || ariaCurrent === "true" || variant === "default" ? "default" : "outline"}
      disabled={disabled || busy}
      aria-current={ariaCurrent}
      aria-busy={
        !threadHandoff && applying && applyingMotion === "working"
          ? true
          : undefined
      }
      onClick={(event) => {
        if (!threadHandoff) {
          captureSource(event.currentTarget, applyKey)
        }
        onUse()
      }}
    >
      {applying && applyingMotion ? (
        <LeoIcon
          variant="ambient"
          size="xs"
          state={applyingMotion}
          className="size-3.5"
        />
      ) : null}
      {label}
    </Button>
  )
}
